import { create } from 'zustand';

export interface USSDMenuItem {
  id: string;
  label: string;
  type: 'menu' | 'input' | 'display' | 'action';
  action?: () => void | Promise<void>;
  children?: USSDMenuItem[];
  inputConfig?: {
    placeholder: string;
    maxLength: number;
    validation?: (value: string) => boolean;
    onSubmit?: (value: string) => void | Promise<void>;
  };
}

export interface USSDSession {
  id: string;
  menuStack: string[];
  currentMenu: USSDMenuItem | null;
  inputValue: string;
  isActive: boolean;
  startTime: number;
  expiresAt: number;
}

interface USSDState {
  session: USSDSession | null;
  startSession: (rootMenu: USSDMenuItem) => void;
  endSession: () => void;
  navigateTo: (menuId: string, menu: USSDMenuItem) => void;
  goBack: () => void;
  setInputValue: (value: string) => void;
  submitInput: () => Promise<void>;
  isSessionExpired: () => boolean;
}

const SESSION_TIMEOUT = 60000; // 60 seconds

export const useUSSDState = create<USSDState>((set, get) => ({
  session: null,

  startSession: (rootMenu) => {
    const now = Date.now();
    set({
      session: {
        id: `ussd_${now}`,
        menuStack: [rootMenu.id],
        currentMenu: rootMenu,
        inputValue: '',
        isActive: true,
        startTime: now,
        expiresAt: now + SESSION_TIMEOUT,
      },
    });
  },

  endSession: () => {
    set({ session: null });
  },

  navigateTo: (menuId, menu) => {
    const { session } = get();
    if (!session) return;

    set({
      session: {
        ...session,
        menuStack: [...session.menuStack, menuId],
        currentMenu: menu,
        inputValue: '',
      },
    });
  },

  goBack: () => {
    const { session } = get();
    if (!session || session.menuStack.length <= 1) return;

    const newStack = session.menuStack.slice(0, -1);
    // In a real implementation, you'd reconstruct the menu from the stack
    set({
      session: {
        ...session,
        menuStack: newStack,
        inputValue: '',
      },
    });
  },

  setInputValue: (value) => {
    const { session } = get();
    if (!session) return;

    set({
      session: {
        ...session,
        inputValue: value,
      },
    });
  },

  submitInput: async () => {
    const { session } = get();
    if (!session || !session.currentMenu) return;

    const menu = session.currentMenu;
    if (menu.type === 'input' && menu.inputConfig?.onSubmit) {
      const isValid = menu.inputConfig.validation?.(session.inputValue) ?? true;
      if (isValid) {
        await menu.inputConfig.onSubmit(session.inputValue);
      }
    }
  },

  isSessionExpired: () => {
    const { session } = get();
    if (!session) return true;
    return Date.now() > session.expiresAt;
  },
}));
